import {
  Box,
  Button,
  Flex,
  Icon,
  InlineNotification,
  TableIconButton,
  Td,
  Text,
  Tr,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import React, { FC, useState } from "react";
import { DeleteButtonNew } from "../../../modules/DeleteButtonNew";
import TableBasedPage from "../../templates/TableBasedPage";
import Link from "../../Link";
import ClassroomsPurgeModal from "./ClassroomsPurgeModal";
import AddClassroom from "./AddClassroom";
import { Classroom } from "../../../types/Classrooms";
import { deleteClassroom, searchClassrooms } from "../../../utils/api/Classrooms";
import { useSessionStore } from "../../../storez/SessionStore";
import { ImportCSVModal } from "./CSVImport/NewScheduleImports/ImportCSVModal";
import { IdentitySearch, IdentitySearchParams } from "../IdentitySearch/IdentitySearch";
import { useCallback } from "react";

export const ClassroomsNew: FC = () => {
  const { t } = useTranslation();

  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [visibleImportCsv, setVisibleImportCsv] = useState(false);
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();
  const PAGE_SIZE = 25;

  const handleClickImport = () => {
    setVisibleImportCsv(true);
  };

  const handleClickCloseImport = () => {
    setVisibleImportCsv(false);
  };

  const { successToast, errorToast } = useToast();
  const handleDelete = async (id: string) => {
    try {
      await deleteClassroom(id);
      successToast({ title: "Success!", description: "The classroom has been deleted." });
      setLoading(false);
      const index = classrooms.findIndex((classoom: Classroom) => classoom.id === id);
      classrooms.splice(index, 1);
      setClassrooms(classrooms);
      setLoading(true);
    } catch (error) {
      errorToast({
        title: "Error",
        description: "We're not able to process the request at this time. Please try again later.",
      });
    }
  };

  const handleSearchError = () => {
    errorToast({ title: "Error", description: "Failed to load. Please refresh the page", isClosable: true });
  };

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Classroom Manager"), url: "/config/device/classwize", isActive: false },
    { title: t("Classrooms"), isActive: true },
  ];

  const columns = [
    { headerText: t("Name"), columnName: "name", sortable: true, searchable: true },
    { headerText: t("Provider"), columnName: "sourceType", sortable: true, searchable: true },
    { headerText: t("Operations"), columnName: "operations", sortable: false, searchable: false },
  ];

  const operations = (row: Classroom): JSX.Element => {
    const { sourceType, id } = row;

    if (sourceType !== undefined && (sourceType.toLowerCase() === "local" || sourceType.toLowerCase() === "csv")) {
      return (
        <Flex>
          <Box mr="sp8">
            <Link to={`/config/device/classwize/classrooms/${encodeURIComponent(id)}`}>
              <TableIconButton
                data-testid="classrooms-edit-button"
                aria-label="Edit"
                icon={<Icon icon="fa-pencil" variant="solid" color="text.paragraph.light" />}
              />
            </Link>
          </Box>
          <Box mr="sp8">
            <DeleteButtonNew
              onClick={() =>
                void (async () => {
                  await handleDelete(id);
                })()
              }
              titleToDelete="Classroom"
              bodyToDelete="classroom"
            />
          </Box>
        </Flex>
      );
    }

    return (
      <Link to={`/config/device/classwize/classrooms/${encodeURIComponent(id)}`}>
        <TableIconButton
          data-testid="classrooms-view-button"
          aria-label="View"
          icon={<Icon icon="fa-eye" variant="solid" color="text.paragraph.light" />}
        />
      </Link>
    );
  };

  const tableDataMap = (row: Classroom, index: number): JSX.Element => {
    const { name, sourceType, id } = row;

    return (
      <Tr key={index}>
        <Td data-testid="classroom-name">
          <Link to={`/config/device/classwize/classrooms/${encodeURIComponent(id)}`}>
            <Text fontSize={"sm"}>{name}</Text>
          </Link>
        </Td>
        <Td data-testid="classroom-provider">
          <Text>{sourceType}</Text>
        </Td>
        <Td>{operations(row)}</Td>
      </Tr>
    );
  };

  const aboveTableChildren = (): JSX.Element => {
    return (
      <Flex mt="sp24" mr="sp24" justifyContent="end">
        <AddClassroom />
        <Box ml="sp8">
          <Button variant="primary" onClick={handleClickImport}>
            {t("Import CSV")}
          </Button>
        </Box>
        {isSupportAdmin && (
          <Box ml="sp8">
            <ClassroomsPurgeModal />
          </Box>
        )}
      </Flex>
    );
  };

  const search = useCallback((params: IdentitySearchParams): Promise<Classroom[]> => {
    return searchClassrooms(params.query, params.pageSize);
  }, []);

  const withinTableChildren = (): JSX.Element => {
    return (
      <>
        <ImportCSVModal open={visibleImportCsv} onClose={handleClickCloseImport} />
        <Flex>
          <IdentitySearch
            setState={setClassrooms}
            setLoading={setLoading}
            searchFn={search}
            pageSize={PAGE_SIZE}
            onError={handleSearchError}
          />
          {!loading && (
            <Box ml="sp12" minW="300px" maxH="sp12">
              <InlineNotification
                notificationDescription="Please use search to display more classrooms."
                notificationTitle={`Showing you ${classrooms.length} classroom(s)`}
                status="info"
              />
            </Box>
          )}
        </Flex>
      </>
    );
  };

  return (
    <TableBasedPage
      title="Classrooms"
      loaded={!loading}
      breadcrumbs={breadcrumbs}
      columns={columns}
      data={classrooms}
      tableDataMap={tableDataMap}
      children={aboveTableChildren()}
      childrenInTableHeader={withinTableChildren()}
      showSearch={false}
    />
  );
};
